<template>
  <img
    ref="imgRef"
    class="zoom-pan-image"
    :src="src"
    :alt="alt"
    draggable="false"
    :style="imgStyle"
    @load="onLoad"
  />
</template>

<script setup>
import panzoom from "panzoom";
import { onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, default: "Preview Image" }
});

const emit = defineEmits(["load"]);

// Refs
const imgRef = ref(null);

// Reactive state
const hasLoaded = ref(false);
const minScale = ref(1);
const maxScale = ref(3); // 3x zoom to show 1/9 of image (1/3 width × 1/3 height)
const cleanup = ref(null);
let pzInstance = null;

// Mobile-only styles for panzoom
const imgStyle = {
  touchAction: "none",
  transformOrigin: "0 0"
};

// Calculate max zoom to show 1/9 of image (1/3 width × 1/3 height)
const calculateMaxZoom = () => {
  if (!imgRef.value) return 3;

  const $img = imgRef.value;
  const container = $img.parentElement;
  if (!container) return 3;

  // Safeguard against division by zero
  const containerRect = container.getBoundingClientRect();
  if (containerRect.width === 0 || containerRect.height === 0) return 3;

  // Use natural image dimensions instead of displayed dimensions
  const { naturalWidth } = $img;
  const { naturalHeight } = $img;

  if (naturalWidth === 0 || naturalHeight === 0) return 3;

  // Calculate zoom needed to fill container with 1/3 of image width/height
  // To show 1/3 of image in container: zoom = (natural_dimension / 3) / container_dimension
  const zoomX = naturalWidth / 3 / containerRect.width;
  const zoomY = naturalHeight / 3 / containerRect.height;

  // Use the larger zoom factor to ensure we don't show more than 1/9
  const calculatedZoom = Math.max(zoomX, zoomY, 1);

  // Return the calculated zoom (no artificial minimum enforcement)
  return calculatedZoom;
};

// Image load handler
const onLoad = () => {
  hasLoaded.value = true;

  // Update max zoom based on actual image dimensions
  maxScale.value = calculateMaxZoom();

  // If panzoom instance exists, dispose and recreate with new max zoom
  if (pzInstance) {
    try {
      pzInstance.dispose();
    } catch {
      // eslint-disable-next-line no-console
      console.error("Error disposing panzoom for zoom update");
    }

    // Recreate with updated max zoom
    try {
      pzInstance = panzoom(imgRef.value, {
        maxZoom: maxScale.value,
        minZoom: minScale.value,
        bounds: true,
        boundsPadding: 0
      });
    } catch {
      // eslint-disable-next-line no-console
      console.error("Error reinitializing panzoom with updated zoom");
    }
  }

  emit("load");
};

// Lifecycle hooks - Mobile only component
onMounted(() => {
  const $img = imgRef.value;
  if (!$img) return;

  try {
    // Wait for image to be fully loaded before calculating zoom constraints
    const initializePanzoom = () => {
      if ($img.complete && $img.naturalHeight !== 0) {
        // Calculate max zoom based on image dimensions
        maxScale.value = calculateMaxZoom();

        pzInstance = panzoom($img, {
          maxZoom: maxScale.value,
          minZoom: minScale.value,
          bounds: true,
          boundsPadding: 0
        });
      } else {
        // If image not loaded yet, use default and update later in onLoad
        pzInstance = panzoom($img, {
          maxZoom: maxScale.value,
          minZoom: minScale.value,
          bounds: true,
          boundsPadding: 0
        });
      }
    };

    initializePanzoom();
  } catch {
    // eslint-disable-next-line no-console
    console.error("Error initializing panzoom");
  }

  cleanup.value = () => {
    try {
      if (pzInstance && typeof pzInstance.dispose === "function") pzInstance.dispose();
    } catch {
      // eslint-disable-next-line no-console
      console.error("Error disposing panzoom");
    }
  };
});

onBeforeUnmount(() => {
  if (cleanup.value) cleanup.value();
});
</script>

<style scoped>
.zoom-pan-image {
  user-select: none;
}
</style>
